import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { PanneauAccueilIa, useAccueilIaAutorise, useCompteInterneAffiche } from './AccueilIaChat';
import ImpersonationContext from '../contexts/ImpersonationContext';
import BuildVersionBadge, { useVersionPortail } from './sidebar/BuildVersionBadge';
import { VAR_DROITE } from './aideAncree';

// ── RAIL DROIT (28/10/2026, demande William) ─────────────────────────────────
// « Le petit chat […] on ne peut pas le déplacer, il me gêne souvent » : le bouton ? de l'aide, la bulle
// d'accueil IA et la pastille de version ne flottent plus sur la page. Ils sont rangés dans une fine barre
// collée au bord droit de la fenêtre, dans le back-office seulement (App.js, BackofficeLayout).
//  - Le bouton ? appartient à HelpPanel : le rail lui prête un emplacement (onSlotAide), où HelpPanel le
//    rend par un portail ; sans emplacement (rail replié, petit écran), il reprend sa place flottante.
//  - La largeur réservée est aussi publiée en variable CSS sur <html> (--rail-droit-largeur, 0 sans rail) :
//    les éléments FIXES ancrés à droite (volet du classement, toasts…) s'en décalent.
//  - Aperçu « comme un client » : le haut du rail descend sous le bandeau rouge d'impersonation ; l'état est
//    suivi EN DIRECT (contexte d'impersonation) : « Revenir à mon compte » remonte le rail sans recharger.
//  - Comptes CLIENT (externes) : pas de rail (décision du 28/10/2026) — ils gardent le ? flottant d'avant,
//    sans largeur perdue. Le rail n'existe que pour un compte affiché INTERNE, ou pour le superadmin.
//  - Écran large (≥ 1025 px) : rail de 40 px, la page s'en resserre (onWidthChange → marge droite de <main>) ;
//    replié, il devient une languette de 12 px, toujours déduite : rien ne passe sous le rail.
//    L'aide ancrée à droite (HelpPanel) se range à GAUCHE du rail.
//  - Petit écran : une languette de 18 px au bord, qui ne prend aucune largeur à la page ; un clic déplie
//    les icônes par-dessus, le temps de s'en servir.
//  - Replié / déplié est mémorisé par utilisateur ; la place de la fenêtre du chat aussi (accolée au rail,
//    ou détachée et déplacée à la souris).
export const RAIL_LARGEUR = 40;
export const LANGUETTE_LARGEUR = 12;
export const LANGUETTE_PETIT_ECRAN = 18;
export const RAIL_LARGEUR_MIN_ECRAN = 1025;   // même seuil que l'aide ancrée
export const VAR_RAIL = '--rail-droit-largeur';
export const HAUTEUR_BANDEAU_APERCU = 56;     // ImpersonationBanner (fixe, en haut, par-dessus tout)
const ECART_PANNEAU = 8;
const FENETRE_VISIBLE_MIN = 120;              // une fenêtre détachée garde au moins 120 px dans l'écran

export function cleRail(user) {
  return `meteris_rail_droit:${user?.id || user?.email || 'anonyme'}`;
}

export function cleFenetreChat(user) {
  return `meteris_accueil_ia_fenetre:${user?.id || user?.email || 'anonyme'}`;
}

function lire(cle) {
  try {
    const brut = JSON.parse(window.localStorage.getItem(cle) || 'null');
    return brut && typeof brut === 'object' ? brut : null;
  } catch {
    return null;
  }
}

function ecrire(cle, valeur) {
  try { window.localStorage.setItem(cle, JSON.stringify(valeur)); } catch { /* stockage indisponible : réglage de session */ }
}

export function lireRail(cle) {
  return { replie: lire(cle)?.replie === true };
}

export function lireFenetre(cle) {
  const brut = lire(cle);
  const x = Number.isFinite(brut?.x) ? brut.x : null;
  const y = Number.isFinite(brut?.y) ? brut.y : null;
  return { detache: brut?.detache === true && x !== null && y !== null, x, y };
}

/** Largeur que la page laisse au rail : 40 px déplié, 12 px replié, 0 sur petit écran ou sans rail. */
export function largeurReservee({ present, grandEcran, replie }) {
  if (!present || !grandEcran) return 0;
  return replie ? LANGUETTE_LARGEUR : RAIL_LARGEUR;
}

/** Fenêtre détachée : ramenée dans l'écran (fenêtre agrandie puis réduite, réglage d'un autre écran…). */
export function bornerFenetre(x, y, largeurEcran, hauteurEcran) {
  return {
    x: Math.round(Math.max(0, Math.min(largeurEcran - FENETRE_VISIBLE_MIN, x))),
    y: Math.round(Math.max(0, Math.min(hauteurEcran - 48, y))),
  };
}

/** Panneau ACCOLÉ : contre le rail, et à gauche de l'aide ancrée à droite (jamais par-dessus). */
export function placementAccole(railPx) {
  return { right: `calc(${railPx + ECART_PANNEAU}px + var(${VAR_DROITE}, 0px))`, bottom: 12 };
}

const BOUTON_RAIL = {
  width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', padding: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default function RailDroit({ onWidthChange, onSlotAide, avecAide = false }) {
  const { user, isImpersonating } = useAuth();
  // L'état d'aperçu se lit dans le contexte (mis à jour au clic sur « Revenir à mon compte ») ; hors de son
  // fournisseur seulement, dans useAuth (lecture du sessionStorage au rendu).
  const impersonation = useContext(ImpersonationContext);
  const apercu = impersonation ? impersonation.isImpersonating : Boolean(isImpersonating);
  const interne = useCompteInterneAffiche();
  const chatAutorise = useAccueilIaAutorise();
  const version = useVersionPortail();                  // superadmin seulement
  // Version : place gardée pendant le chargement (la page ne saute pas), rendue si la version reste illisible.
  const versionPresente = version.etat === 'chargement' || version.etat === 'ok';
  const autorise = interne || user?.role === 'superadmin';
  const present = autorise && (avecAide || chatAutorise || versionPresente);
  const haut = apercu ? HAUTEUR_BANDEAU_APERCU : 0;

  const cle = cleRail(user);
  const [replie, setReplie] = useState(() => lireRail(cle).replie);
  useEffect(() => { setReplie(lireRail(cle).replie); }, [cle]);

  const [largeurEcran, setLargeurEcran] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [hauteurEcran, setHauteurEcran] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  useEffect(() => {
    const onResize = () => { setLargeurEcran(window.innerWidth); setHauteurEcran(window.innerHeight); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const grandEcran = largeurEcran >= RAIL_LARGEUR_MIN_ECRAN;
  const [deplieSurPetitEcran, setDeplieSurPetitEcran] = useState(false);

  const reservee = largeurReservee({ present, grandEcran, replie });
  useEffect(() => {
    onWidthChange?.(reservee);
    document.documentElement.style.setProperty(VAR_RAIL, `${reservee}px`);
  }, [reservee, onWidthChange]);
  useEffect(() => () => {
    onWidthChange?.(0);
    document.documentElement.style.removeProperty(VAR_RAIL);
  }, [onWidthChange]);

  // ── Chat : ouvert / réduit, accolé / détaché (place mémorisée par utilisateur) ──
  const [chatOuvert, setChatOuvert] = useState(false);
  const cleFenetre = cleFenetreChat(user);
  const [fenetre, setFenetre] = useState(() => lireFenetre(cleFenetre));
  useEffect(() => { setFenetre(lireFenetre(cleFenetre)); }, [cleFenetre]);
  const boutonChat = useRef(null);
  const languette = useRef(null);
  const railSuperpose = useRef(null);
  const boutonReplier = useRef(null);
  // Replier / déplier remplace le bouton cliqué par son contraire : le focus clavier le suit (après rendu).
  const focusApres = useRef(null);
  useEffect(() => {
    if (!focusApres.current) return;
    const cible = focusApres.current === 'replier' ? boutonReplier.current : languette.current;
    focusApres.current = null;
    cible?.focus();
  });
  const glisse = useRef(null);
  const [glisser, setGlisser] = useState(false);

  const changerFenetre = useCallback((maj) => {
    setFenetre((prec) => {
      const suivant = { ...prec, ...maj };
      ecrire(cleFenetre, suivant);
      return suivant;
    });
  }, [cleFenetre]);

  const changerReplie = (valeur) => {
    focusApres.current = valeur ? 'languette' : 'replier';
    setReplie(valeur);
    ecrire(cle, { replie: valeur });
  };

  const ouvrirOuReduireChat = () => {
    setChatOuvert((o) => !o);
    if (!grandEcran) setDeplieSurPetitEcran(false);   // petit écran : le rail s'efface devant le panneau
  };

  const reduireChat = () => {
    setChatOuvert(false);
    (boutonChat.current || languette.current)?.focus();   // le focus revient d'où l'on a ouvert
  };

  const detacher = () => {
    if (fenetre.x !== null && fenetre.y !== null) { changerFenetre({ detache: true }); return; }
    const rect = document.querySelector('[data-testid="accueil-ia-panneau"]')?.getBoundingClientRect();
    changerFenetre({ detache: true, ...bornerFenetre(rect?.left || 0, rect?.top || 0, window.innerWidth, window.innerHeight) });
  };

  const debutGlisser = (e) => {
    if (e.button !== 0 || e.target.closest('button')) return;
    e.preventDefault();
    // On part de la place AFFICHÉE (ramenée dans l'écran), pas de la valeur mémorisée brute.
    const depart = bornerFenetre(fenetre.x || 0, fenetre.y || 0, window.innerWidth, window.innerHeight);
    glisse.current = { x0: e.clientX, y0: e.clientY, fx: depart.x, fy: depart.y, derniere: null };
    setGlisser(true);
  };

  useEffect(() => {
    if (!glisser) return undefined;
    const onMove = (e) => {
      const g = glisse.current;
      g.derniere = bornerFenetre(g.fx + e.clientX - g.x0, g.fy + e.clientY - g.y0, window.innerWidth, window.innerHeight);
      setFenetre((prec) => ({ ...prec, ...g.derniere }));
    };
    const onUp = () => {
      setGlisser(false);
      if (glisse.current?.derniere) changerFenetre(glisse.current.derniere);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [glisser, changerFenetre]);

  // Petit écran : Échap replie les icônes dépliées par-dessus la page.
  useEffect(() => {
    if (!deplieSurPetitEcran) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { focusApres.current = 'languette'; setDeplieSurPetitEcran(false); }
    };
    // … et un clic à l'extérieur aussi.
    const onClicDehors = (e) => {
      if (railSuperpose.current && !railSuperpose.current.contains(e.target)) setDeplieSurPetitEcran(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClicDehors);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClicDehors);
    };
  }, [deplieSurPetitEcran]);

  if (!present) return null;

  // Largeur VISIBLE du rail, contre laquelle s'accole le panneau du chat.
  const visible = grandEcran ? reservee : LANGUETTE_PETIT_ECRAN;
  const position = bornerFenetre(fenetre.x || 0, fenetre.y || 0, largeurEcran, hauteurEcran);
  const placement = fenetre.detache ? { left: position.x, top: position.y } : placementAccole(visible);

  const icones = (
    <>
      {/* Emplacement du bouton ? (rendu par HelpPanel, portail) */}
      {avecAide && <div ref={onSlotAide} data-rail-slot="aide" style={{ display: 'flex' }} />}
      {chatAutorise && (
        <button
          ref={boutonChat}
          type="button"
          data-testid="rail-chat"
          onClick={ouvrirOuReduireChat}
          aria-expanded={chatOuvert}
          aria-label={chatOuvert ? "Réduire l'assistante d'accueil" : "Ouvrir l'assistante d'accueil"}
          title="Assistante d'accueil Atelier Météris (test interne)"
          style={{ ...BOUTON_RAIL, background: chatOuvert ? '#115e59' : '#0f766e', color: '#fff' }}
        >
          <MessageCircle size={18} aria-hidden="true" />
        </button>
      )}
      <div style={{ flex: 1 }} />
      {versionPresente && <BuildVersionBadge version={version} />}
    </>
  );

  const replier = (
    <button
      type="button"
      ref={boutonReplier}
      onClick={() => {
        if (grandEcran) { changerReplie(true); return; }
        focusApres.current = 'languette';
        setDeplieSurPetitEcran(false);
      }}
      aria-label="Replier la barre d'outils"
      title="Replier la barre d'outils"
      style={{ ...BOUTON_RAIL, height: 24, background: 'transparent', color: 'var(--muted, #6b7d8f)' }}
    >
      <ChevronRight size={16} aria-hidden="true" />
    </button>
  );

  let rail;
  if (grandEcran && !replie) {
    rail = (
      <nav
        data-testid="rail-droit" data-etat="deplie" aria-label="Barre d'outils"
        style={{
          position: 'fixed', top: haut, right: 0, bottom: 0, width: RAIL_LARGEUR, zIndex: 85, boxSizing: 'border-box',
          background: 'var(--surface, #fff)', borderLeft: '1px solid var(--border-light, #e5e7eb)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '8px 0 12px',
        }}
      >
        {replier}
        {icones}
      </nav>
    );
  } else if (grandEcran) {
    rail = (
      <button
        ref={languette}
        type="button" data-testid="rail-droit" data-etat="replie"
        onClick={() => changerReplie(false)}
        aria-label="Déplier la barre d'outils" title="Déplier la barre d'outils (assistante, version)"
        style={{
          position: 'fixed', top: haut, right: 0, bottom: 0, width: LANGUETTE_LARGEUR, zIndex: 85, padding: 0,
          background: 'var(--surface, #fff)', border: 'none', borderLeft: '1px solid var(--border-light, #e5e7eb)',
          color: 'var(--muted, #6b7d8f)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ChevronLeft size={12} aria-hidden="true" />
      </button>
    );
  } else if (deplieSurPetitEcran) {
    rail = (
      <nav
        ref={railSuperpose}
        data-testid="rail-droit" data-etat="superpose" aria-label="Barre d'outils"
        style={{
          position: 'fixed', right: 0, bottom: 160, width: RAIL_LARGEUR, zIndex: 85, boxSizing: 'border-box',
          background: 'var(--surface, #fff)', border: '1px solid var(--border-light, #e5e7eb)', borderRight: 'none',
          borderRadius: '10px 0 0 10px', boxShadow: 'var(--shadow-lg, 0 4px 16px rgba(0,0,0,0.15))',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '6px 0 8px',
        }}
      >
        {replier}
        {icones}
      </nav>
    );
  } else {
    rail = (
      <button
        ref={languette}
        type="button" data-testid="rail-droit" data-etat="languette"
        onClick={() => { focusApres.current = 'replier'; setDeplieSurPetitEcran(true); }}
        aria-label="Déplier la barre d'outils" title="Barre d'outils (assistante, version)"
        style={{
          position: 'fixed', right: 0, bottom: 160, width: LANGUETTE_PETIT_ECRAN, height: 48, zIndex: 85, padding: 0,
          background: 'var(--surface, #fff)', border: '1px solid var(--border-light, #e5e7eb)', borderRight: 'none',
          borderRadius: '8px 0 0 8px', boxShadow: 'var(--shadow-lg, 0 4px 16px rgba(0,0,0,0.15))',
          color: 'var(--muted, #6b7d8f)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>
    );
  }

  return (
    <>
      {rail}
      {/* Monté tant que le chat est autorisé : réduire garde la conversation en cours */}
      {chatAutorise && (
        <PanneauAccueilIa
          ouvert={chatOuvert}
          onReduire={reduireChat}
          placement={placement}
          detache={fenetre.detache}
          onDetacher={detacher}
          onRattacher={() => changerFenetre({ detache: false })}
          onPoigneeMouseDown={debutGlisser}
        />
      )}
    </>
  );
}
